import React, { useEffect, useState } from 'react';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Award, ExternalLink, Mail, Megaphone } from 'lucide-react';
import { SponsorBanner } from '@/components/SponsorBanner';
import PageSideRail from '@/components/PageSideRail';
import RichText from '@/components/RichText';

const BACKEND_URL = '';

export default function SponsorsPage({ user }) {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/sponsors`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        setSponsors(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(error => {
        console.error(error);
        setLoading(false);
      });
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      
      <main className="pt-24 pb-12 px-4 sm:px-6">
        <div className="container mx-auto max-w-7xl">
          <SponsorBanner placement="app" type="top" className="mb-6 flex justify-center" />
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1 min-w-0">
          <div className="mb-8">
            <h1 className="text-4xl font-bold font-heading mb-2 gradient-text">Sponsors & Exhibitors</h1>
            <p className="text-slate-600">Meet our trusted partners driving sustainability forward</p>
          </div>

          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {sponsors.map(sponsor => (
                <Card
                  key={sponsor.sponsor_id}
                  data-testid={`sponsor-card-${sponsor.sponsor_id}`}
                  className={`p-8 shadow-xl shadow-slate-200/50 hover:shadow-2xl transition-all ${
                    sponsor.has_active_ad
                      ? 'border-2 border-green-400 bg-green-50/30 ring-1 ring-green-200'
                      : 'border border-slate-100'
                  }`}
                >
                  {sponsor.has_active_ad && (
                    <div className="flex items-center gap-1.5 mb-4" data-testid={`sponsor-active-badge-${sponsor.sponsor_id}`}>
                      <Megaphone className="h-3.5 w-3.5 text-green-600" />
                      <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                        Active Advertiser
                        {sponsor.earliest_campaign_end && ` \u00b7 Ends ${sponsor.earliest_campaign_end}`}
                      </span>
                    </div>
                  )}
                  <div className="mb-6">
                    {sponsor.logo_url ? (
                      <img src={sponsor.logo_url} alt={sponsor.name} className="h-16 object-contain mb-4" />
                    ) : (
                      <Award className="h-16 w-16 text-brand-navy mb-4" />
                    )}
                    <h3 className="text-2xl font-bold font-heading mb-3">{sponsor.name}</h3>
                    <RichText html={sponsor.description} className="text-slate-600 card card-3" />
                  </div>

                  {sponsor.categories?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {sponsor.categories.map((cat, idx) => (
                        <span key={idx} className="px-3 py-1 bg-brand-sage/10 text-brand-sage text-xs rounded-full">
                          {cat}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="space-y-2">
                    {sponsor.website && (
                      <a href={sponsor.website} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" className="w-full">
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Visit Website
                        </Button>
                      </a>
                    )}
                    {sponsor.contact_email && (
                      <a href={`mailto:${sponsor.contact_email}`}>
                        <Button variant="outline" className="w-full">
                          <Mail className="h-4 w-4 mr-2" />
                          Contact
                        </Button>
                      </a>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}

          {!loading && sponsors.length === 0 && (
            <div className="text-center py-16">
              <p className="text-slate-500">No sponsors listed yet</p>
            </div>
          )}
            </div>
            <PageSideRail placement="app" />
          </div>
        </div>
      </main>
    </div>
  );
}
